import React from 'react';
import { ExternalLink, Sparkles, BookOpen, Zap, Headphones, MessageSquare, Video, Award, Globe } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ToolsForTransformation = () => {
  const tools = [
    {
      name: 'Emergent',
      description: 'Build your own AI Movement with Emergent',
      url: 'https://app.emergent.sh/register?ref=wp20124971',
      cta: 'Get Started with Emergent →',
    },
    {
      name: 'Juicer',
      description: 'Display your social feeds live on your site',
      url: 'https://bit.ly/SocialMediaDisplays',
      cta: 'Free Starter Plan →',
      price: 'Free Starter Plan'
    },
    {
      name: 'MakerAI',
      description: 'AI-Powered Content Creation',
      url: 'https://makerai.com/?via=charles',
      cta: 'Try MakerAI →',
    },
    {
      name: 'Magic Clips',
      description: 'Turn Videos into Viral Clips',
      url: 'https://magicclips.com/?via=charles',
      cta: 'Try Magic Clips →',
    },
    {
      name: 'Lead Hero',
      description: 'Capture & Convert Leads',
      url: 'https://leadhero.com/?via=charles',
      cta: 'Try Lead Hero →',
    },
    {
      name: 'Hermetix',
      description: 'Ancient Wisdom Meets Modern Tech',
      url: 'https://hermetix.com/?via=charles',
      cta: 'Try Hermetix →',
    },
    {
      name: 'Starter Story',
      description: 'Learn from Successful Founders',
      url: 'https://starterstory.com/?via=charles',
      cta: 'Try Starter Story →',
    },
    {
      name: 'TidyCal',
      description: 'Simple Scheduling Solution',
      url: 'https://tidycal.com/?via=charles',
      cta: 'Try TidyCal →',
    },
    {
      name: 'Durable',
      description: 'AI Website Builder',
      url: 'https://durable.co/?via=charles',
      cta: 'Try Durable →',
    },
    {
      name: 'Hypefury',
      description: 'Social Media Growth Tool',
      url: 'https://hypefury.com/?via=charles',
      cta: 'Try Hypefury →',
    },
    {
      name: 'Podia',
      description: 'Sell Courses & Digital Products',
      url: 'https://podia.com/?via=charles',
      cta: 'Try Podia →',
    },
  ];

  return (
    <section id="tools" className="py-24 bg-gradient-to-b from-[#0B1D2A] to-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6 backdrop-blur-sm">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
              Curated Resources
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Tools for <span className="text-[#D4AF37]">Transformation</span>
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full mb-6" />
          <p
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            Resources and platforms I personally use and recommend for personal growth, leadership development, and conscious living.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool, index) => (
            <Card
              key={index}
              className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-6 hover:border-[#D4AF37] transition-all duration-300 hover:transform hover:scale-105 backdrop-blur-sm group"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                    <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                </div>
                
                <h3
                  className="text-white font-bold text-xl mb-3"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {tool.name}
                </h3>
                
                <p
                  className="text-white/70 text-sm leading-relaxed mb-6 flex-grow"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  {tool.description}
                </p>
                
                <Button
                  asChild
                  className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-semibold transition-all duration-300"
                >
                  <a
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    {tool.cta}
                  </a>
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-16">
          <Card className="bg-[#0B1D2A]/40 border-[#D4AF37]/10 p-6 backdrop-blur-sm">
            <p
              className="text-white/60 text-sm text-center leading-relaxed"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <strong className="text-white/80">Transparency Note:</strong> Some links above are affiliate partnerships, meaning I may earn a small commission at no extra cost to you. I only recommend tools and platforms I personally use and believe in. Your support helps sustain this work.
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ToolsForTransformation;
